/**
 * App shell — routes between class select, the live run, and the Chronicle.
 *
 * All game logic lives in @lastroll/engine; this file only decides what to show
 * for the session's current `phase`.
 */

import { useCallback, useState } from 'react';
import type { ClassId, DiceFace, GameSession } from '@lastroll/engine';
import { useGame } from './hooks/useGame.js';
import { ClassSelect } from './components/ClassSelect.js';
import { Hud } from './components/Hud.js';
import { DiceTray } from './components/DiceTray.js';
import { Board } from './components/Board.js';
import { EncounterPanel } from './components/EncounterPanel.js';
import { CombatPanel } from './components/CombatPanel.js';
import { Chronicle } from './components/Chronicle.js';
import { Feed } from './components/Feed.js';
import { Hand } from './components/Hand.js';
import { FxLayer } from './components/FxLayer.js';
import { DevNotice } from './components/DevNotice.js';
import { PromoBar } from './components/PromoBar.js';

export function App(): JSX.Element {
  const game = useGame();
  const [muted, setMuted] = useState(false);
  const { session } = game;

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      game.audio.setMuted(!m);
      return !m;
    });
  }, [game.audio]);

  const handleStart = useCallback(
    (classId: ClassId, seed: string) => game.start(classId, seed),
    [game],
  );

  if (!session) {
    return (
      <div className="app">
      <PromoBar />
        <div className="stage">
          <ClassSelect onStart={handleStart} onLoad={game.load} hasSave={game.hasSave} />
        </div>
        {/* 랜딩에서만 — 판 도중에는 띄우지 않는다. */}
        <DevNotice />
        {game.error && <Toast message={game.error} onDismiss={game.dismissError} />}
      </div>
    );
  }

  const phase = session.phase;
  const inEncounter = phase === 'encounter' || phase === 'dialogue';
  const pending = session.pendingRoll;

  return (
    <div className="app">
      <PromoBar />
      <Hud
        session={session}
        muted={muted}
        onToggleMute={toggleMute}
        onSave={game.save}
        onQuit={game.reset}
      />

      <main className="stage">
        <div className="wrap">
          {phase === 'runOver' ? (
            <Chronicle session={session} onRestart={game.reset} />
          ) : (
            <>
              {(phase === 'awaitMove' || phase === 'awaitRoll') && (
                <Board session={session} onMove={game.moveTo} />
              )}

              {pending && (
                <DiceTray
                  session={session}
                  rolling={game.rolling}
                  freeRerolls={session.freeRerolls}
                  onManipulate={game.manipulate}
                  compact={phase === 'awaitMove'}
                />
              )}

              {inEncounter && (
                <EncounterPanel
                  session={session}
                  onChoose={(id: string, face?: DiceFace | null) => game.choose(id, face ?? null)}
                  onWait={game.hesitate}
                  onRollForChoice={() => game.roll('choice')}
                />
              )}

              {phase === 'combat' && (
                <CombatPanel
                  session={session}
                  onFight={game.fight}
                  onRoll={() => game.roll('combat')}
                />
              )}

              <Feed events={game.feed} />
            </>
          )}
        </div>

        {phase !== 'runOver' && <Hand session={session} onPlay={game.playCard} />}
      </main>

      {phase === 'awaitRoll' && !pending && (
        <div className="actionbar">
          <div className="actionbar__row">
            {session.currentRoom?.rest && <RestButton session={session} onRest={game.rest} />}
            <button type="button" className="roll-btn" onClick={() => game.roll('movement')}>
              🎲 주사위를 굴린다
            </button>
          </div>
        </div>
      )}

      {/* 굴린 눈으로 갈 곳이 없을 때 — 굴림 단추가 있던 자리에 둔다. 없으면 화면이 멈췄다. */}
      {phase === 'awaitMove' && session.stayBlocked() === null && (
        <div className="actionbar">
          <div className="actionbar__row">
            <button type="button" className="roll-btn stay-btn" onClick={game.stay}>
              🎲 이 눈은 흘려보낸다 — 다시 굴린다
            </button>
          </div>
        </div>
      )}

      {/* 절대 규칙 11: 만들었으면 화면에 띄운다. FX 5종이 실제로 그려지는 곳. */}
      <FxLayer cue={game.fx} />

      {game.error && <Toast message={game.error} onDismiss={game.dismissError} />}
    </div>
  );
}

/**
 * 쉴 곳에서만 보이는 단추 (GDD §12-1). 쉴 수 없으면 걸어 두고 까닭을 적는다 — 숨기지 않는다.
 * 한 판에 방마다 한 번이라, 쓰고 나면 「여기서는 이미 한 번 잤다」가 남는다.
 */
function RestButton({ session, onRest }: { session: GameSession; onRest: () => void }): JSX.Element {
  const blocked = session.restBlocked();
  return (
    <button
      type="button"
      className="roll-btn roll-btn--ghost rest-btn"
      onClick={onRest}
      disabled={blocked !== null}
      title={blocked ?? '주사위를 굴려 쉰다 — 1 은 악몽, 6 은 깊은 잠. 이 방에서는 한 번뿐.'}
    >
      <span>🔥 쉬어 간다</span>
      <small className="rest-btn__note">{blocked ?? '한 번뿐 · 1 악몽 · 6 깊은 잠'}</small>
    </button>
  );
}

function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }): JSX.Element {
  return (
    <button type="button" className="toast" onClick={onDismiss}>
      {message}
    </button>
  );
}
